import {
  ChangeDetectorRef,
  Component,
  Inject,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  DfFormFieldBuilder,
  DfFormFieldConfig,
  DfTemplateOptions,
} from '@lib/fresco';
import { UserContentModalBaseDirective } from '@app/user-content/user-input/user-content-modal-base/user-content-modal-base.directive';

import { InputsService } from '@app/inputs/services/inputs.service';
import { TipService } from '@app/onboarding/services/tip.service';
import { AuthService } from '@app/shared/services/auth.service';
import { WindowToken } from '@app/shared/window.token';
import { TranslateService } from '@ngx-translate/core';
import { UploaderService } from '@app/uploader/uploader.service';
import { DurationConverterService } from '@app/shared/services/duration-converter.service';

import { UserImageFieldComponent } from '@app/form-fields/wrappers/user-image-field/user-image-field.component';
import { UploadPictureAndCropResponse } from '@app/uploader/uploader-api.model';
import { InputImageUpload } from '@app/uploader/upload-section/adapters/input-image-upload.adapter';
import { MediaEntry, MediaParameters } from '@app/inputs/inputs-api.model';
import { TagsApi } from '@app/tags/tag-api.model';
import { GroupIdentifier } from '@app/groups/group-api';
import { FormControl } from '@angular/forms';
import { UploadAdapter } from '@app/uploader/upload-section/upload-adapter';
import { camelCaseKeys } from '@app/shared/utils/property';

export interface FeedVideoFormModel {
  title: string;
  description: string;
  imageData: Partial<UploadPictureAndCropResponse>;
  length: number;
  groups: GroupIdentifier[];
  tags: TagsApi.Tag[];
}

/** A modal form for entering video Input data after a user finishes recording a video
 * in-browser. */
@Component({
  selector: 'dgx-feed-video-modal',
  templateUrl: 'feed-video-modal.component.html',
  providers: [
    {
      provide: UploadAdapter,
      useFactory: (service: UploaderService) =>
        new InputImageUpload(service, 'Video'),
      deps: [UploaderService],
    },
  ],
})
export class FeedVideoModalComponent extends UserContentModalBaseDirective<
  MediaEntry,
  FeedVideoFormModel,
  MediaParameters
> {
  @ViewChild('skillsView') public skillsViewRef: TemplateRef<any>;
  @ViewChild('visibility') public visibilityRef: TemplateRef<any>;

  public i18n = this.translate.instant([
    'MediaFormCtrl_SaveVideo',
    'dgOrgInternalContentForm_GroupsTitle',
    'dgOrgInternalContentForm_GroupsToolTip',
    'OrgInternalContent_ValidateGroupSelectionRequired',
  ]);
  public visibleToOrg = true;

  constructor(
    @Inject(WindowToken) windowRef: Window,
    cdr: ChangeDetectorRef,
    activeModal: NgbActiveModal,
    authService: AuthService,
    tipService: TipService,
    inputsService: InputsService,
    private uploadAdapter: UploadAdapter,
    private translate: TranslateService,
    private builder: DfFormFieldBuilder,
    public durationConverter: DurationConverterService
  ) {
    super(
      windowRef,
      cdr,
      activeModal,
      authService,
      tipService,
      inputsService,
      translate.instant('OrgInternalContent_AddNewDisplayVideo')
    );
  }

  // HACK: These two inefficient accessors temporarily provide "unzipped" group data compatible with the refactor-wanting group visibility control
  public get selectedGroupNames() {
    return this.model.groups.map((g) => g.name);
  }

  public get selectedGroupIds() {
    return this.model.groups.map((g) => g.groupId);
  }

  public handleTagsChange(control: FormControl, tags) {
    FeedVideoModalComponent.setCustomFieldValue(control, tags);
  }

  public handleSetVisibilityValues({
    groupIds,
    groupNames,
  }: {
    groupIds: number[];
    groupNames: string[];
  }) {
    const groupIdsControl = this.form.get('groups');
    // HACK: re-zip the group data from org visibility component
    const groups: GroupIdentifier[] = groupIds.map((groupId, i) => ({
      groupId,
      name: groupNames[i],
    }));
    groupIdsControl.setValue(groups);
  }

  // TODO: More digging is needed to understand the need for this better. The migrated orgs visibility component
  // may not need this behavior in ngx but is used in several places already.
  public setGroupsDirtyState = (isDirty: boolean): void => {
    const groupsControl = this.form.get('groups');
    isDirty ? groupsControl.markAsDirty() : groupsControl.markAsPristine();
  };

  public handleGroupsAdd = ({ model }) => {
    const groupIdentifier: GroupIdentifier = camelCaseKeys(model);
    const control = this.form.get('groups') as FormControl;
    const groups = [groupIdentifier, ...control.value];
    FeedVideoModalComponent.setCustomFieldValue(control, groups);
  };

  public handleGroupsRemove = ({ model }) => {
    const groupIdentifier: GroupIdentifier = camelCaseKeys(model);
    const control = this.form.get('groups') as FormControl;
    let groups: GroupIdentifier[] = control.value;
    const removeAt = groups.findIndex(
      (g) => g.groupId === groupIdentifier.groupId
    );
    groups = [...groups.slice(0, removeAt), ...groups.slice(removeAt + 1)];
    FeedVideoModalComponent.setCustomFieldValue(control, groups);
  };

  protected buildFormFields(): DfFormFieldConfig<DfTemplateOptions>[] {
    return [
      // title
      this.builder
        .title()
        .withDgatId('feedVideoModal-a34')
        .autofocused()
        .build(),
      // video length
      this.builder
        // group soley for formatting length field as column of 1/2 row width
        .fieldGroup(
          '',
          [
            this.builder
              .requiredTextInput(
                'length',
                'dgOrgInternalContentForm_VideoLength'
              )
              .ofType('number')
              .withMin(0)
              .withDgatId('feedVideoModal-07e')
              .styledBy('df-form__col-half')
              .build(),
          ],
          'df-form__row'
        )
        .build(),
      // description
      this.builder.descriptionLong().withDgatId('feedVideoModal-c80').build(),
      // image upload
      this.builder
        .foreignField(
          'imageData',
          'dgOrgInternalContentForm_AddImage',
          UserImageFieldComponent.REGISTERED_FIELD_TYPE,
          {
            isHostImageUrl: false,
            useCropper: true,
            uploadAdapter: this.uploadAdapter,
            provideImageUrl: (control: FormControl) => {
              const response: UploadPictureAndCropResponse = control.value;
              return response.legacyPictureUrl || response.pictureUrl;
            },
          }
        )
        .build(),
      // skills
      this.builder
        .customField('tags', 'Core_Skills', this.skillsViewRef, {
          topTags: this.userInterests,
        })
        .unwrapped() // The tag editor has its own label and doesn't require validation, so present it sans fresco's default field wrapper
        .build(),
      // group visibility
      this.builder
        .customField(
          'groups',
          'orgInternalContentVisibilitySelector_Title',
          this.visibilityRef
        )
        .asOptional()
        .validatedByIndexed({
          groupsValidation: {
            expression: (control) => {
              // when restricting to specific non-org groups at least one group must be specified
              const isValid = this.visibleToOrg || control.value?.length > 0;
              return isValid;
            },
            message:
              this.i18n.OrgInternalContent_ValidateGroupSelectionRequired,
          },
        })
        .build(),
    ];
  }

  protected initFormModel(source: Partial<MediaEntry>) {
    this.model = {
      title: source.title,
      length: this.durationConverter.fromSecondsToMinutes(source.mediaLength),
      description: source.summary,
      imageData: {
        pictureUrl: source.imageUrl ?? '',
      },
      groups: [],
      tags: [],
    };
  }

  protected createResult(
    model: Partial<FeedVideoFormModel>,
    defaults: Partial<MediaEntry>
  ): MediaParameters {
    return {
      inputType: 'Video',
      title: model.title,
      mediaLength: this.durationConverter.fromMinutesToSeconds(model.length),
      summary: model.description,
      imageUrl: model.imageData.legacyPictureUrl || model.imageData.pictureUrl,
      resourceImageId: model.imageData.resourceImageId,
      tags: model.tags,
      entryUrl: defaults.entryUrl,
      orgContentMetadata: {
        groupIds: model.groups,
      },
      organizationId:
        defaults.organizationId || this.authUser.defaultOrgInfo.organizationId,
      // scenario: 'Feed', ??
      useQuickCheck: false,
    };
  }
}
